import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, CheckSquare, Target, FileText, Zap, TrendingUp, Bot, Settings } from 'lucide-react';
import { User } from '../../types';
import { usePermissions } from '../../hooks/usePermissions';
import { PageKey } from '../../types/permissions';

interface QuickActionsBarProps {
  currentUser?: User;
  orgId?: string;
  onAddLead: () => void;
  onAddTask: () => void;
  onAddOpportunity: () => void;
  onCreateOffer: () => void;
  onNavigate: (view: string) => void;
  onOpenAgentMode?: () => void;
  onOpenMCPAdmin?: () => void;
}

const QuickActionsBar: React.FC<QuickActionsBarProps> = ({
  currentUser,
  orgId,
  onAddLead,
  onAddTask,
  onAddOpportunity,
  onCreateOffer,
  onNavigate,
  onOpenAgentMode,
  onOpenMCPAdmin
}) => {
  const { t } = useTranslation('dashboard');
  
  const { canWrite } = usePermissions(currentUser, orgId);

  const allQuickActions = [
    { 
      id: 'lead', 
      label: t('quickActions.addLead'), 
      icon: UserPlus, 
      onClick: onAddLead,
      iconColor: 'text-accent',
      permission: 'leads' as PageKey
    },
    { 
      id: 'task', 
      label: t('quickActions.addTask'), 
      icon: CheckSquare, 
      onClick: onAddTask,
      iconColor: 'text-txt-secondary',
      permission: 'tasks' as PageKey
    },
    { 
      id: 'opportunity', 
      label: t('quickActions.addOpportunity'), 
      icon: Target, 
      onClick: onAddOpportunity,
      iconColor: 'text-txt-secondary',
      permission: 'opportunities' as PageKey
    },
    { 
      id: 'offer', 
      label: t('quickActions.createOffer'), 
      icon: FileText, 
      onClick: onCreateOffer,
      iconColor: 'text-txt-secondary',
      permission: 'offers' as PageKey
    }
  ];

  const quickActions = allQuickActions.filter(action => canWrite(action.permission));

  const quickNavigation = [
    { label: 'Hot Leads', icon: TrendingUp, view: 'leads', color: 'text-danger' },
    { label: 'Pipeline', icon: Zap, view: 'opportunities', color: 'text-accent' }
  ];

  return (
    <div className="bg-bg-card/90 backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-card border-0 p-4 sm:p-6 hover:shadow-card transition-shadow duration-200 space-y-4">
      {/* Agent Mode - Featured prominently */}
      {onOpenAgentMode && (
        <button
          onClick={onOpenAgentMode}
          className="w-full p-4 bg-gradient-to-r from-accent to-accent hover:from-accent hover:to-accent rounded-xl transition-all duration-200 hover:shadow-card hover:scale-[1.02] active:scale-95 group relative overflow-hidden"
        >
          <div className="relative z-10 flex items-center justify-center space-x-3">
            <Bot className="w-6 h-6 text-txt-inverted group-hover:scale-110 transition-transform" />
            <div className="text-left">
              <div className="text-lg font-bold text-txt-inverted">Agent Mode</div>
              <div className="text-xs text-txt-inverted/90">Voice-powered CRM assistant</div>
            </div>
            <div className="ml-auto">
              <span className="px-3 py-1 bg-bg-card/20 backdrop-blur-sm rounded-full text-xs font-medium text-txt-inverted">
                Ctrl+Shift+A
              </span>
            </div>
          </div>
          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-r from-accent to-accent opacity-0 group-hover:opacity-20 transition-opacity" />
        </button>
      )}

      {/* Quick Add Buttons */}
      {quickActions.length > 0 && (
        <div className={`grid ${quickActions.length === 1 ? 'grid-cols-1' : quickActions.length === 2 ? 'grid-cols-2' : quickActions.length === 3 ? 'grid-cols-3' : 'grid-cols-2 lg:grid-cols-4'} gap-3 sm:gap-4`}>
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={action.onClick}
              className="p-3 sm:p-4 bg-bg-surface hover:bg-bg-card border-0 rounded-lg lg:rounded-xl transition-all duration-200 hover:shadow-card hover:scale-105 active:scale-95 group"
            >
              <action.icon className={`w-5 h-5 mx-auto mb-2 ${action.iconColor} group-hover:scale-110 transition-transform`} />
              <div className="text-xs sm:text-sm font-semibold text-txt">{action.label}</div>
            </button>
          ))}
        </div>
      )}

      {/* MCP Admin - For admins only */}
      {onOpenMCPAdmin && (
        <button
          onClick={onOpenMCPAdmin}
          className="w-full p-3 bg-bg-surface hover:bg-bg-surface border border-default rounded-lg transition-all duration-200 hover:shadow-card group"
        >
          <div className="flex items-center justify-center space-x-2 text-txt-secondary">
            <Settings className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
            <span className="text-sm font-medium">MCP Configuration</span>
            <span className="px-2 py-0.5 bg-bg-surface-hover rounded text-xs text-txt-secondary">Ctrl+Shift+M</span>
          </div>
        </button>
      )}
    </div>
  );
};

export default QuickActionsBar;
